import { forEach } from '@angular/router/src/utils/collection';
import { Inject, Injectable, Optional } from '@angular/core';
import { Headers, Http, URLSearchParams } from '@angular/http';

import { Observable } from 'rxjs/Observable';

import 'rxjs/add/operator/map';
import 'rxjs/add/operator/timeout';

import { CookieService } from 'app/services/cookie.service';
import { Base64 } from 'app/shared/base64-encoder';

import { ConstantsService } from 'app/services/constants-service';
import { RequestOptions } from '@angular/http/src/base_request_options';
import { ResponseContentType } from '@angular/http/src/enums';

/**
 * Generic service for sending HTTP requests that returns an Observable as response.
 */
@Injectable()
export class ObservableRequestService {
  private applicationURL = '';

  constructor(private http: Http, private cookieService: CookieService,
    @Inject('isAuthApp') @Optional() public isAuthApp?: boolean) {

    this.applicationURL = isAuthApp ? ConstantsService.AUTH_BE_URL : ConstantsService.APP_BE_URL;
    this.applicationURL += '/rest';
  }


  getRequestWithOutCredentials(requestUrl: string): Observable<any> {
    return this.http.get(requestUrl)
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  postRequestWithOutCredentials(requestUrl: string, body: Object, header: Headers): Observable<any> {
    return this.http
      .post(requestUrl, body, {headers: header})
      .map(response => response.json() as Object)
      .timeout(300000);
  }

  // This method will be used for all subsequent requests except initial login. The request header
  // created here will contain the access token received on successful login.
  getRequest(requestUrl: string, params?: URLSearchParams): Observable<any> {
    const url = this.applicationURL + requestUrl;
    const header = this.getJSONContentType();
    return this.http
      .get(url, { headers: this.addCommonHeaders(header), search: params })
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  downloadFile(requestUrl: string): Observable<Blob> {
    const headers = new Headers();
    const options = new RequestOptions({ responseType: ResponseContentType.Blob, headers: this.addCommonHeaders(headers) });

    return this.http.get(this.applicationURL + requestUrl, options)
      .map(res => res.blob()).catch(error => Observable.throw(error));

  }

  downloadFileWithName(requestUrl: string, fallbackFileName: string): Observable<{ blob: Blob, fileName: string }> {
    const headers = new Headers();
    const options = new RequestOptions({ responseType: ResponseContentType.Blob, headers: this.addCommonHeaders(headers) });

    return this.http.get(this.applicationURL + requestUrl, options)
      .map(res => ({
        blob: res.blob(),
        fileName: this.extractFileNameFromContentDisposition(res.headers.get('Content-Disposition')) || fallbackFileName
      }))
      .catch(error => Observable.throw(error));
  }

  private extractFileNameFromContentDisposition(contentDisposition: string): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
    return match && match[1] ? match[1] : null;
  }

  getRequestWithMapResponse(requestUrl: string): Observable<any> {
    const url = this.applicationURL + requestUrl;
    const header = this.getJSONContentType();
    return this.http
      .get(url, { headers: this.addCommonHeaders(header) })
      .timeout(ConstantsService.TIMEOUT);
  }

  // This method will be used for initial login case only. The request header created here will
  // include auth token.
  getRequestForLogin(requestUrl: string, username: string, password: string): Observable<any> {
    const url = this.applicationURL + requestUrl;
    const header = this.getJSONContentType();
    return this.http
      .get(url, { headers: this.addCommonHeadersForLogin(username, password, header) })
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  requestForReset(requestUrl: string, resetKey: string, password: string): Observable<any> {
    const url = this.applicationURL + requestUrl;
    const header = this.getJSONContentType();
    return this.http
      .get(url, { headers: this.addCommonHeadersForLogin(resetKey, password, header) })
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  postRequest(requestUrl: string, body: Object, header: Headers, timeOut: number = ConstantsService.TIMEOUT): Observable<any> {
    const url = this.applicationURL + requestUrl;
    header = this.addCommonHeaders(header);
    return this.http
      .post(url, body, {headers: header})
      .map(response => response.json() as Object)
      .timeout(timeOut);
  }

  putRequest(requestUrl: string, body: Object, header: Headers): Observable<any> {
    const url = this.applicationURL + requestUrl;
    header = this.addCommonHeaders(header);
    return this.http
      .put(url, body, { headers: header })
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  deleteRequest(requestUrl: string, body: Object, header: Headers): Observable<any> {
    const url = this.applicationURL + requestUrl;
    header = this.addCommonHeaders(header);
    return this.http
      .delete(url, {
        headers: header,
        body: body,
      })
      .map(response => response.json() as Object)
      .timeout(ConstantsService.TIMEOUT);
  }

  getAuthToken(username: string, password: string): string {
    const authToken = this.cookieService.get(ConstantsService.COOKIE_LOGIN_TOKEN);
    if (authToken) {
      return authToken;
    }
    return Base64.encodeBase64(username.concat('/', password));
  }

  getJSONContentType(): Headers {
    return new Headers({ 'Content-Type': 'application/json' });
  }

  getFormEncodedContentType(): Headers {
    return new Headers({ 'Content-Type': 'application/x-www-form-urlencoded' });
  }

  getFormUrlEncoded(toConvert) {
    const formBody = [];
    for (const property in toConvert) {
      const encodedKey = encodeURIComponent(property);
      const encodedValue = encodeURIComponent(toConvert[property]);
      formBody.push(encodedKey + '=' + encodedValue);
    }
    return formBody.join('&');
  }

  private addCommonHeaders(header: Headers): Headers {
    header.append('Authorization',
      'Bearer ' + this.cookieService.get(ConstantsService.COOKIE_ACCESS_TOKEN));
    header = this.addAppType(header);
    return header;
  }

  private addCommonHeadersForLogin(username: string, password: string, header: Headers): Headers {
    header.append('Authorization',
      'Basic ' + this.getAuthToken(username, password));
    header = this.addAppType(header);
    return header;
  }

  private addAppType(header: Headers): Headers {
    // TODO (pankaj.khurana): We can remove App-Type header once angular1.x app is decommissioned.
    header.append('app-type', 'TRUST');
    return header;
  }

  /**
   * Serializes the form element so it can be passed to the back end through the url.
   * The objects properties are the keys and the objects values are the values.
   * ex: { "a":1, "b":2, "c":3 } would look like ?a=1&b=2&c=3
   */
  serialize(body: Object): URLSearchParams {
    const params = new URLSearchParams();

    for (const key in body) {
      if (body.hasOwnProperty(key)) {
        const value = body[key];
        if (value instanceof Array) {
          value.forEach(val => params.append(key, val));
        } else {
          params.append(key, value);
        }
      }
    }
    return params;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/observable-request.service.ts